import { useState } from "react";
import { motion } from "framer-motion";
import { PlayCircle, FileText, Mic, ExternalLink, GraduationCap, Search } from "lucide-react";
import { trendTalks, asdaVideos, publications, articles, podcasts } from "../../data/library";

const rise = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } } };

export default function LibraryTab() {
  const [q, setQ] = useState("");

  const ql = q.trim().toLowerCase();
  const match = (...fields) => !ql || fields.some((f) => f && f.toLowerCase().includes(ql));
  const ft = trendTalks.filter((t) => match(t.title, t.speaker, t.year));
  const fa = asdaVideos.filter((v) => match(v.title, v.speaker));
  const far = articles.filter((a) => match(a.title, a.source, a.year));
  const fp = publications.filter((p) => match(p.title, p.meta));
  const fpod = podcasts.filter((p) => match(p.title, p.guest, p.year));
  const total = ft.length + fa.length + far.length + fp.length + fpod.length;

  return (
    <div className="tab-panel" data-testid="tab-panel-library">
      <div className="panel-wrap cream">
        <div className="eyebrow">Library</div>
        <h2 className="heading">Talks, writing &amp; audio.</h2>
        <p className="work-intro">A public collection of the content Paulyn has created and published over the years — the Trend Talk conversations she hosted, the academy sessions she led, the articles and research she wrote and co-authored, and the podcast she hosts and produces. Free to explore and share.</p>

        <div className="lib-search" data-testid="library-search">
          <Search className="icon" />
          <input type="text" placeholder="Search talks, articles, publications and episodes..." value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="library-search-input" />
          {q && <span className="lib-search-count" data-testid="library-search-count">{total} result{total === 1 ? "" : "s"}</span>}
        </div>
        {q && total === 0 && <p className="lib-empty" data-testid="library-no-results">No results for "{q}". Try a different keyword.</p>}

        <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} style={{ display: ft.length ? undefined : "none" }}>
          <div className="lib-section-head">
            <PlayCircle className="icon" />
            <span>Trend Talk videos · {ft.length}</span>
          </div>
          <p className="lib-byline" data-testid="trendtalk-byline">Conceptualized and hosted by Paulyn Duman — expert conversations on the trends shaping sustainable development.</p>
          <div className="lib-grid">
            {ft.map((t, i) => (
              <a className="lib-card" href={t.url} target="_blank" rel="noopener noreferrer" key={i} data-testid={`trendtalk-${i}`}>
                <div className="lib-year">{t.year}</div>
                <div className="lib-title">{t.title}</div>
                <div className="lib-meta">{t.speaker}</div>
                <div className="lib-link">Watch on Vimeo <ExternalLink className="icon" /></div>
              </a>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="panel-wrap white" style={{ display: fa.length ? undefined : "none" }}>
        <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}>
          <div className="lib-section-head">
            <PlayCircle className="icon" />
            <span>ASDA video sessions · {fa.length}</span>
          </div>
          <p className="lib-section-intro">Recordings from the Africa Sustainable Development Academy — the flagship programme Paulyn designed and led — plenaries, share fairs and sessions on climate security, finance, clean energy and inclusive development. Free to watch and share.</p>
          <div className="lib-grid">
            {fa.map((v, i) => (
              <a className="lib-card" href={v.url} target="_blank" rel="noopener noreferrer" key={i} data-testid={`asda-video-${i}`}>
                <div className="lib-title">{v.title}</div>
                {v.speaker && <div className="lib-meta" data-testid={`asda-video-${i}-speaker`}>{v.speaker}</div>}
                <div className="lib-link">Watch on YouTube <ExternalLink className="icon" /></div>
              </a>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="panel-wrap cream" style={{ display: far.length + fp.length ? undefined : "none" }}>
        <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} style={{ display: far.length ? undefined : "none" }}>
          <div className="lib-section-head">
            <FileText className="icon" />
            <span>Published articles · {far.length}</span>
          </div>
          <p className="lib-byline" data-testid="articles-byline">Written by Paulyn Duman — and in some cases co-authored — for UNSSC, UN Thailand, LinkedIn and other platforms.</p>
          <div className="lib-list">
            {far.map((a, i) => (
              <a className="lib-row" href={a.url} target="_blank" rel="noopener noreferrer" key={i} data-testid={`article-row-${i}`}>
                <span className="lib-row-year">{a.year}</span>
                <span className="lib-row-title">{a.title}</span>
                <span className="lib-row-source">{a.source}</span>
              </a>
            ))}
          </div>
        </motion.div>

        <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} style={{ marginTop: far.length ? "3.5rem" : 0, display: fp.length ? undefined : "none" }}>
          <div className="lib-section-head">
            <GraduationCap className="icon" />
            <span>Research &amp; academic publications · {fp.length}</span>
          </div>
          <p className="lib-byline" data-testid="publications-byline">Research authored and co-authored by Paulyn Duman across public policy, taxation, migration, justice and psychology.</p>
          <div className="lib-pub-grid">
            {fp.map((p, i) => (
              <div className="lib-pub-card" key={i} data-testid={`publication-${i}`}>
                <div className="lib-title">{p.title}</div>
                <div className="lib-meta">{p.meta}</div>
                <div className="lib-pub-note">Full text available on request</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="panel-wrap white" style={{ display: fpod.length ? undefined : "none" }}>
        <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}>
          <div className="lib-section-head">
            <Mic className="icon" />
            <span>SDG Learncast episodes · {fpod.length}</span>
          </div>
          <p className="lib-byline" data-testid="podcast-byline">The SDG Learncast — conceptualized, hosted and produced by Paulyn Duman across five seasons.</p>
          <div className="lib-list">
            {fpod.map((p, i) => (
              <a className="lib-row" href={p.url} target="_blank" rel="noopener noreferrer" key={i} data-testid={`podcast-row-${i}`}>
                <span className="lib-row-year">{p.year}</span>
                <span className="lib-row-title">{p.title}<span className="lib-row-guest">{p.guest}</span></span>
                <span className="lib-row-action">Listen</span>
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
